import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Chip,
  IconButton,
  Fade,
} from '@mui/material';
import {
  Policy as PolicyIcon,
  Update as UpdateIcon,
  ContactMail as ContactIcon,
  ArrowUpward as ArrowUpIcon,
} from '@mui/icons-material';
import { environment } from '../../../environments/environment';
import SectionHeader from '../../components/common/SectionHeader';
// Structured legal content for Terms & Conditions
const legalSections = [
  {
    id: 'terms',
    title: 'Terms & Conditions',
    icon: <PolicyIcon />,
    description: 'Rules and guidelines for using our website and services',
    lastUpdated: '09/24/2025',
    content: [
      {
        subtitle: 'Use of the Website',
        items: [
          'You agree to use the website for lawful purposes only.',
          'You must be at least 18 years old to place an order or use our services.',
          'You may not disrupt or interfere with the website or its services.',
        ],
      },
      {
        subtitle: 'Orders and Payments',
        items: [
          'Orders are subject to availability.',
          'Prices, menu items, and promotions may change without notice.',
          'Payment must be completed at the time of order. Only approved methods are accepted.',
          'By placing an order, you provide accurate and complete information.',
        ],
      },
      {
        subtitle: 'Refunds and Cancellations',
        items: [
          "Refunds or cancellations are subject to the Company's refund policy, which may vary by location.",
          'Contact us promptly for requests.',
        ],
      },
      {
        subtitle: 'Intellectual Property',
        items: [
          'Website content, logos, and designs are owned by the Company or its licensors.',
          'You may not copy or use content for commercial purposes without written consent.',
        ],
      },
      {
        subtitle: 'Limitation of Liability',
        text: 'The Company is not liable for direct, indirect, incidental, or consequential damages from your use of the website or services.',
      },
      {
        subtitle: 'Third-Party Links and Services',
        text: 'We are not responsible for content or practices of third-party websites linked from our site.',
      },
      {
        subtitle: 'Governing Law',
        text: 'Terms are governed by the laws of the state where the operating LLC managing your order is registered.',
      },
    ],
  },
  {
    id: 'updates',
    title: 'Updates',
    icon: <UpdateIcon />,
    description: 'How and when we update our policies',
    content: [
      {
        text: 'We may update this Legal page from time to time. Updates will be effective upon posting with a revised "Last Updated" date. Continued use of the website constitutes acceptance of any changes.',
      },
    ],
  },
  {
    id: 'contact',
    title: 'Contact Us',
    icon: <ContactIcon />,
    description: 'How to reach us with questions or concerns',
    content: [
      {
        text: 'If you have questions about these Terms & Conditions, please contact us:',
        contactInfo: {
          company: 'The Fatty Patty Burger – Asya LLC and Affiliated Entities',
          address: '4260 South 26th St. Milwaukee, WI. 53221',
          email: 'thefattypattymke@gmail.com',
          phone: '1-773-732-1170',
        },
      },
    ],
  },
];
const TermsConditions = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {environment?.restaurantId !== 'fattyPattyDev' &&
        environment?.restaurantId !== 'fattyPatty' && (
          <Box
            sx={{
              width: '100%',
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              pt: '82px',
            }}
          >
            <Container maxWidth="lg" sx={{ py: 4 }}>
              <Typography
                variant="h3"
                sx={{
                  textAlign: 'center',
                  fontWeight: 'bold',
                  color: '#172B4D',
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  mb: 2,
                }}
              >
                Terms & Conditions
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  textAlign: 'center',
                  color: '#6C757D',
                  fontSize: '1.1rem',
                  maxWidth: '600px',
                  mx: 'auto',
                }}
              >
                Rules and guidelines for using our website and services
              </Typography>
            </Container>
          </Box>
        )}
      {(environment?.restaurantId === 'fattyPattyDev' ||
        environment?.restaurantId === 'fattyPatty') && (
        <SectionHeader
          title="Terms & Conditions"
          breadcrumbs={[
            { label: 'Home', path: '/' },
            { label: 'Terms & Conditions', current: true },
          ]}
          fontFamily="Jost"
          showBreadcrumbs={true}
          titleVariant="h2"
          customStyles={{
            title: { fontSize: '40px', fontWeight: '700' },
            container: { minHeight: '250px' },
          }}
        />
      )}

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Header Section */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Chip
            label="Effective Date: September 24, 2025"
            sx={{
              mb: 2,
              backgroundColor: `${environment.mainColor}15`,
              color: environment.mainColor,
              fontWeight: 600,
              fontSize: '0.9rem',
            }}
          />
          <Typography
            variant="body1"
            sx={{
              color: '#6C757D',
              fontSize: '1.1rem',
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.7,
            }}
          >
            These Terms & Conditions govern your use of the Fatty Patty Burger
            website and services, operated by Asya LLC and its affiliated
            entities. By accessing or using our website or services, you agree
            to these terms.
          </Typography>
        </Box>

        {/* Legal Sections */}
        <Box sx={{ mb: 4 }}>
          {legalSections.map((section, index) => (
            <Fade in={true} timeout={300 + index * 100} key={section.id}>
              <Paper
                elevation={0}
                sx={{
                  mb: 3,
                  borderRadius: '16px',
                  border: '1px solid #E5E7EB',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    background: `linear-gradient(135deg, ${environment.mainColor}08, ${environment.mainColor}15)`,
                    borderBottom: `1px solid ${environment.mainColor}20`,
                    py: 2,
                    px: 3,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        backgroundColor: `${environment.mainColor}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        color: environment.mainColor,
                      }}
                    >
                      {section.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: '#172B4D',
                          fontSize: '1.25rem',
                          fontFamily: 'Jost, sans-serif',
                        }}
                      >
                        {section.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#6C757D',
                          fontSize: '0.95rem',
                          mt: 0.5,
                        }}
                      >
                        {section.description}
                      </Typography>
                    </Box>
                    {section.lastUpdated && (
                      <Chip
                        label={`Updated: ${section.lastUpdated}`}
                        size="small"
                        sx={{
                          ml: 2,
                          backgroundColor: '#E8F5E8',
                          color: '#2E7D32',
                          fontSize: '0.75rem',
                        }}
                      />
                    )}
                  </Box>
                </Box>

                {/* Content */}
                <Box sx={{ p: 4 }}>
                  {section.content.map((item, itemIndex) => (
                    <Box key={itemIndex} sx={{ mb: 3 }}>
                      {item.subtitle && (
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: '#172B4D',
                            fontSize: '1.1rem',
                            mb: 1.5,
                            fontFamily: 'Jost, sans-serif',
                          }}
                        >
                          {item.subtitle}
                        </Typography>
                      )}

                      {item.text && (
                        <Typography
                          variant="body1"
                          sx={{
                            color: '#495057',
                            lineHeight: 1.7,
                            mb: item.items ? 1 : 0,
                            fontSize: '1rem',
                          }}
                        >
                          {item.text}
                        </Typography>
                      )}

                      {item.items && (
                        <Box sx={{ ml: 2 }}>
                          {item.items.map((listItem, listIndex) => (
                            <Box
                              key={listIndex}
                              sx={{ display: 'flex', mb: 1 }}
                            >
                              <Box
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  backgroundColor: environment.mainColor,
                                  mt: 1,
                                  mr: 2,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                variant="body1"
                                sx={{
                                  color: '#495057',
                                  lineHeight: 1.7,
                                  fontSize: '1rem',
                                }}
                              >
                                {listItem}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {item.contactInfo && (
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            mt: 2,
                            borderRadius: '12px',
                            backgroundColor: '#F8F9FA',
                            border: '1px solid #E9ECEF',
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: '#172B4D',
                              mb: 2,
                              fontFamily: 'Jost, sans-serif',
                            }}
                          >
                            Contact Information
                          </Typography>

                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 600, color: '#172B4D' }}
                            >
                              {item.contactInfo.company}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ color: '#495057' }}
                            >
                              <span role="img" aria-label="Address">
                                📍
                              </span>{' '}
                              {item.contactInfo.address}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ color: '#495057' }}
                            >
                              <span role="img" aria-label="Email">
                                ✉️
                              </span>{' '}
                              {item.contactInfo.email}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ color: '#495057' }}
                            >
                              <span role="img" aria-label="Phone">
                                📞
                              </span>{' '}
                              {item.contactInfo.phone}
                            </Typography>
                          </Box>
                        </Paper>
                      )}
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Fade>
          ))}
        </Box>

        {/* Footer Note */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: '16px',
            backgroundColor: `${environment.mainColor}08`,
            border: `1px solid ${environment.mainColor}20`,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: '#495057',
              fontSize: '1rem',
              lineHeight: 1.7,
              fontStyle: 'italic',
            }}
          >
            This document was last updated on September 24, 2025. We recommend
            reviewing this page periodically to stay informed about the terms
            that govern your use of our services.
          </Typography>
        </Paper>
      </Container>
    </>
  );
};

export default TermsConditions;
